import { AddProductsToCartMutation } from '@notino/shared/definitions/types';

import {
  AddToCartProductErrors,
  ProductWithCount,
} from '@sharedComponents/AddToCartButton/model';

import { generateNotificationForProduct, parseStatuses } from '../../../utils';

interface IAddToCartResult {
  response: AddProductsToCartMutation['AddProductsToCart'];
  products: ProductWithCount[];
}

export type StatusMessages = NonNullable<
  ReturnType<typeof addToCartResult>['statusMessages']
>;

export const addToCartResult = ({ response, products }: IAddToCartResult) => {
  const fatalErrors = getFatalErrors(response.productAddResponse ?? []);

  const productById = (id: string) =>
    response.productAddResponse?.find((p) => p && String(p.productId) === id);

  if (fatalErrors.length === 0) {
    const statusMessages = products
      .map((product) => {
        const { productIncrease } = productById(product.id) ?? {
          productIncrease: 0,
        };

        return parseStatuses(
          response.serviceAddResponse,
          productIncrease,
          product.count
        );
      })
      .flat();

    return {
      success: true,
      statusMessages,
    };
  }

  const notificationMessages = createNotification(products, productById);

  return {
    success: false,
    errorCode: fatalErrors[0],
    notification: notificationMessages[0],
  };
};

function createNotification(
  products: IAddToCartResult['products'],
  productById: (
    id: string
  ) =>
    | NonNullable<
        AddProductsToCartMutation['AddProductsToCart']['productAddResponse']
      >[number]
    | undefined
) {
  return products
    .map((product) => {
      const productFatalErrors = (
        productById(product.id)?.messages ?? []
      ).filter(onlyFatalErrors);

      if (productFatalErrors.length === 0) {
        return null;
      }

      return generateNotificationForProduct(productFatalErrors, product);
    })
    .filter(Boolean);
}

const nonFatalAddToCartProductErrors = [
  AddToCartProductErrors.SC_INSURANCE_ADD_ERROR,
  AddToCartProductErrors.SC_ECO_PACKAGING_ADD_ERROR,
];

const onlyFatalErrors = (m: AddToCartProductErrors) =>
  !nonFatalAddToCartProductErrors.includes(m);

const getFatalErrors = (
  products:
    | AddProductsToCartMutation['AddProductsToCart']['productAddResponse']
    | null
) =>
  (products ?? [])
    .filter(
      (product): product is NonNullable<typeof product> => product !== null
    )
    .map((product) => product.messages)
    .flat()
    .filter(onlyFatalErrors) as AddToCartProductErrors[];
